/**
 * playerIndex — look a full player record back up from a composite key.
 *
 * WHY THIS EXISTS. A Draft Plan Target stores only what a plan needs to be
 * self-describing: { key, name, pos, team, market, price, edited, gone }. It
 * carries no sleeper_id, so it cannot render a headshot on its own. Adding one
 * to the Target would mean a migration for every plan already in localStorage,
 * to denormalize a field that is already available for free from static data —
 * and would go stale the next time the player pool is regenerated.
 *
 * So the plan keeps storing the key and looks the rest up at render time. The
 * key is the SAME composite `${name}-${pos}` that dynamicPrices and targetKey
 * use, so there is one identity for a player across the whole feature.
 *
 * Built once at module load. UNIQUE_PLAYERS is a static import that never
 * changes at runtime, so there is nothing to invalidate and no hook to write.
 */

import { UNIQUE_PLAYERS } from "../data/players";

const INDEX = new Map();
for (const p of UNIQUE_PLAYERS) {
  INDEX.set(`${p.name}-${p.pos}`, p);
}

/**
 * The full player record for a composite key, or null.
 *
 * A miss is normal, not exceptional: a plan can outlive a player leaving the
 * pool. Callers pass the Target itself as the fallback — it carries name, pos
 * and team, which is everything the avatar needs for its initials circle.
 */
export function getPlayerByKey(key) {
  if (!key) return null;
  return INDEX.get(key) || null;
}

/** Escape hatch for diagnostics; do not mutate. */
export const playerIndex = INDEX;
